/* eslint-disable */
import React, { useEffect, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { reactSelectStyle } from "Config/Config";
import * as url from "helper/UrlHelper";
import {
  getData,
  postData,
  putData,
  uploadMultipleFile,
  uploadSingleFile,
} from "utils/Gateway";
import { fetchOptions } from "helper/Common/CommonHelper";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";
import { useTranslation } from "react-i18next";
import FileUploadField from "components/Common/FileUploadField/FileUploadField";

// Import medical data
import { interventionTypeOptions } from "data/medical/interventionTypes";
import { roleOptions } from "data/medical/roles";
import { medicineOptions } from "data/medical/medicines";
import { labTestOptions } from "data/medical/labTests";
import { imagingOptions } from "data/medical/imaging";
import { therapyOptions } from "data/medical/therapies";
import { allergyOptions } from "data/medical/allergies";
import { pastMedicalIssuesOptions } from "data/medical/pastMedicalIssues";
import { treatmentStatusOptions } from "data/medical/treatmentstatus";
import { typeOptions } from "data/medical/treatmenttype";
import { assetImages } from "constants";
import ClinicDetailsCard from "components/Common/ClinicDetailsCard/ClinicDetailsCard";
import PatientDetailsCard from "components/Common/PatientDetailsCard/PatientDetailsCard";
import DoctorDetailsCard from "components/Common/DoctorDetailsCard/DoctorDetailsCard";

// Helper function to format date to YYYY-MM-DD
const formatDateToInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to format date for display (DD-MM-YYYY)
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

// Helper function to validate progress value (0-100)
const validateProgress = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const numValue = parseInt(value);
  if (isNaN(numValue)) return 0;
  if (numValue < 0) return 0;
  if (numValue > 100) return 100;
  return numValue;
};

// Helper function to map saved values to option objects
const mapValuesToOptions = (savedValues, optionsList) => {
  if (!savedValues || !Array.isArray(savedValues)) return [];

  return savedValues
    .map((savedValue) => {
      const foundOption = optionsList.find(
        (option) => option.value === savedValue
      );
      return foundOption || { value: savedValue, label: savedValue }; // fallback if not found in options
    })
    .filter(Boolean);
};

// Helper function to map single value to option object
const mapValuesToSingleOption = (savedValue, optionsList) => {
  if (!savedValue) return null;

  const foundOption = optionsList.find((option) => option.value === savedValue);
  return foundOption || { value: savedValue, label: savedValue }; // fallback if not found in options
};

// Step Modal component
const StepModal = ({
  isOpen,
  onClose,
  onSave,
  editingStep,
  phaseIndex,
  phaseStartDate, // NEW: incoming phase start date for auto-population
  phaseDays, // NEW: incoming phase days for auto-calculation
}) => {
  const [id, setId] = useState(null);
  const [title, setTitle] = useState("");
  const [interventiontype, setInterventiontype] = useState(null);
  const [durationdays, setDurationdays] = useState("");
  const [instructiontopatient, setInstructiontopatient] = useState("");
  const [medicines, setMedicines] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [imaging, setImaging] = useState([]);
  const [therapies, setTherapies] = useState([]);
  const [startdate, setStartdate] = useState(""); // NEW
  const [enddate, setEnddate] = useState(""); // NEW
  const [progress, setProgress] = useState(0); // NEW

  useEffect(() => {
    if (editingStep) {
      setId(editingStep._id || null);
      setTitle(editingStep.title || "");
      setInterventiontype(editingStep.interventiontype || null);
      setDurationdays(editingStep.durationdays || "");
      setInstructiontopatient(editingStep.instructiontopatient || "");
      setMedicines(editingStep.medicines || []);
      setLabTests(editingStep.labTests || []);
      setImaging(editingStep.imaging || []);
      setTherapies(editingStep.therapies || []);
      setStartdate(editingStep.startdate || ""); // NEW: restore existing step dates
      setEnddate(editingStep.enddate || ""); // NEW
      setProgress(editingStep.progress || 0); // NEW: restore existing progress
    } else {
      resetForm();
    }
  }, [editingStep, isOpen]);

  const resetForm = () => {
    setId(null);
    setTitle("");
    setInterventiontype(null);
    setDurationdays("");
    setInstructiontopatient("");
    setMedicines([]);
    setLabTests([]);
    setImaging([]);
    setTherapies([]);
    setStartdate(""); // NEW
    setEnddate(""); // NEW
    setProgress(0); // NEW
  };

  // AUTO-POPULATE: If step has no startdate but phase provides one, use it.
  useEffect(() => {
    if (!editingStep && !startdate && phaseStartDate && isOpen) {
      // Only auto-populate for new steps (not editing) when modal opens
      setStartdate(phaseStartDate);
    }
  }, [phaseStartDate, isOpen, editingStep]); // run when modal opens or phase date changes

  // Additional effect to handle case when step is being edited but has no start date
  useEffect(() => {
    if (editingStep && !startdate && phaseStartDate) {
      // If editing step has no start date, use phase start date
      setStartdate(phaseStartDate);
    }
  }, [editingStep, phaseStartDate]);

  // AUTO-CALCULATE enddate whenever startdate or durationdays changes.
  useEffect(() => {
    // prefer step's durationdays, fallback to phaseDays if step duration empty
    const daysToUse = durationdays || phaseDays;
    if (startdate && daysToUse) {
      try {
        const startDateObj = new Date(startdate);
        const endDateCalc = new Date(startDateObj);
        endDateCalc.setDate(startDateObj.getDate() + parseInt(daysToUse));
        const calculatedEndDate = formatDateToInput(endDateCalc);
        if (enddate !== calculatedEndDate) {
          setEnddate(calculatedEndDate);
        }
      } catch (err) {
        // invalid date - ignore
      }
    }
  }, [startdate, durationdays, phaseDays, enddate]);

  const handleSave = () => {
    const stepData = {
      _id: id,
      title,
      interventiontype,
      durationdays: parseInt(durationdays) || 0,
      instructiontopatient,
      medicines,
      labTests,
      imaging,
      therapies,
      startdate, // NEW
      enddate, // NEW
      progress: progress === "" ? 0 : progress, // NEW
    };

    onSave(stepData, phaseIndex, editingStep?.stepIndex);
    resetForm();
    onClose();
  };

  // Custom styles for white background
  const whiteSelectStyle = {
    ...reactSelectStyle("3px", "6px", true, false),
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
      borderColor: "#dee2e6",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "white",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#007bff" : "white",
      "&:hover": {
        backgroundColor: state.isSelected ? "#007bff" : "#f8f9fa",
      },
    }),
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              {editingStep ? "Edit Step" : "Add Step"}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body">
            {/* Step Name - Single Row */}
            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="d-block fw-semibold mb-2">Step Name *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: "white" }}
                  placeholder="Enter step name"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">
                  Type of Intervention *
                </label>
                <Select
                  options={interventionTypeOptions}
                  value={interventiontype}
                  onChange={setInterventiontype}
                  isClearable
                  placeholder="Select intervention type"
                  styles={whiteSelectStyle}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">
                  Duration (Days) *
                </label>
                <input
                  type="number"
                  min="1"
                  value={durationdays}
                  onChange={(e) => setDurationdays(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: "white" }}
                  placeholder="Enter duration in days"
                />
              </div>
            </div>

            {/* NEW: Start Date and End Date inputs */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">Start Date</label>
                <input
                  type="date"
                  value={startdate}
                  onChange={(e) => setStartdate(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: "white" }}
                />
                <small className="text-muted">
                  If empty, will default to phase start date (if available).
                </small>
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">End Date</label>
                <input
                  type="date"
                  value={enddate}
                  onChange={(e) => setEnddate(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: "white" }}
                  title="Auto-calculated from start date and duration. You can override if needed."
                />
                <small className="text-muted">
                  Auto-calculated from Start Date + Duration (days)
                </small>
              </div>
            </div>

            {/* Conditional Select Fields Based on Intervention Type */}
            {/* {interventiontype?.value === 'medication' && (
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Medicines</label>
                  <Select
                    isMulti
                    options={medicineOptions}
                    value={medicines}
                    onChange={setMedicines}
                    isClearable
                    placeholder="Select medicines"
                    styles={whiteSelectStyle}
                  />
                </div>
              </div>
            )}

            {interventiontype?.value === 'lab_test' && (
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Lab Tests</label>
                  <Select
                    isMulti
                    options={labTestOptions}
                    value={labTests}
                    onChange={setLabTests}
                    isClearable
                    placeholder="Select lab tests"
                    styles={whiteSelectStyle}
                  />
                </div>
              </div>
            )}

            {interventiontype?.value === 'imaging' && (
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Imaging Studies</label>
                  <Select
                    isMulti
                    options={imagingOptions}
                    value={imaging}
                    onChange={setImaging}
                    isClearable
                    placeholder="Select imaging studies"
                    styles={whiteSelectStyle}
                  />
                </div>
              </div>
            )}

            {interventiontype?.value === 'therapy' && (
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Therapies</label>
                  <Select
                    isMulti
                    options={therapyOptions}
                    value={therapies}
                    onChange={setTherapies}
                    isClearable
                    placeholder="Select therapies"
                    styles={whiteSelectStyle}
                  />
                </div>
              </div>
            )} */}

            {/* Show all relevant fields for consultation, follow-up, education, etc. */}
            {/* {(interventiontype?.value === 'consultation' || 
              interventiontype?.value === 'follow_up' || 
              interventiontype?.value === 'surgery') && (
              <>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="d-block fw-semibold mb-2">Medicines (if any)</label>
                    <Select
                      isMulti
                      options={medicineOptions}
                      value={medicines}
                      onChange={setMedicines}
                      isClearable
                      placeholder="Select medicines"
                      styles={whiteSelectStyle}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="d-block fw-semibold mb-2">Lab Tests (if any)</label>
                    <Select
                      isMulti
                      options={labTestOptions}
                      value={labTests}
                      onChange={setLabTests}
                      isClearable
                      placeholder="Select lab tests"
                      styles={whiteSelectStyle}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="d-block fw-semibold mb-2">Imaging (if any)</label>
                    <Select
                      isMulti
                      options={imagingOptions}
                      value={imaging}
                      onChange={setImaging}
                      isClearable
                      placeholder="Select imaging studies"
                      styles={whiteSelectStyle}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="d-block fw-semibold mb-2">Therapies (if any)</label>
                    <Select
                      isMulti
                      options={therapyOptions}
                      value={therapies}
                      onChange={setTherapies}
                      isClearable
                      placeholder="Select therapies"
                      styles={whiteSelectStyle}
                    />
                  </div>
                </div>
              </>
            )} */}

            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="d-block fw-semibold mb-2">
                  Instructions to Patient
                </label>
                <textarea
                  rows="4"
                  value={instructiontopatient}
                  onChange={(e) => setInstructiontopatient(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: "white" }}
                  placeholder="Enter instructions for the patient..."
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!title || !interventiontype || !durationdays}
            >
              Save Step
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Block component to display steps within phases
const StepBlock = ({ step, stepIndex, phaseIndex, editStep, deleteStep }) => {
  return (
    <div className="border rounded-6 p-3 mb-2 bg-light position-relative">
      <div className="position-absolute top-0 end-0 p-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary me-1"
          onClick={() => editStep(phaseIndex, stepIndex)}
          title="Edit Step"
          style={{ width: "28px", height: "28px", padding: "0" }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "14px" }}
          >
            edit
          </span>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={() => deleteStep(phaseIndex, stepIndex)}
          title="Delete Step"
          style={{ width: "28px", height: "28px", padding: "0" }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "14px" }}
          >
            delete
          </span>
        </button>
      </div>

      <div className="row">
        <div className="col-md-8">
          <h6 className="fw-semibold text-primary mb-1">{step.title}</h6>
          <p className="mb-1 fs-sm">
            <strong>Type:</strong> {step.interventiontype?.label}
          </p>
          {/* NEW: Display start and end dates */}
          {(step.startdate || step.enddate) && (
            <p className="mb-1 fs-sm">
              <strong>Timeline:</strong>
              {step.startdate && (
                <span className="text-success">
                  {" "}
                  Start: {formatDateForDisplay(step.startdate)}
                </span>
              )}
              {step.startdate && step.enddate && <span> | </span>}
              {step.enddate && (
                <span className="text-warning">
                  {" "}
                  End: {formatDateForDisplay(step.enddate)}
                </span>
              )}
            </p>
          )}
          <p className="mb-1 fs-sm">
            <strong>Duration:</strong> {step.durationdays} days
          </p>
        </div>
        <div className="col-md-4">
          {step.medicines && step.medicines.length > 0 && (
            <div className="mb-2">
              <strong className="fs-sm">Medicines:</strong>
              <ul className="fs-sm mb-0 mt-1">
                {step.medicines.map((med, idx) => (
                  <li key={idx}>{med.label}</li>
                ))}
              </ul>
            </div>
          )}
          {step.labTests && step.labTests.length > 0 && (
            <div className="mb-2">
              <strong className="fs-sm">Lab Tests:</strong>
              <ul className="fs-sm mb-0 mt-1">
                {step.labTests.map((test, idx) => (
                  <li key={idx}>{test.label}</li>
                ))}
              </ul>
            </div>
          )}
          {step.imaging && step.imaging.length > 0 && (
            <div className="mb-2">
              <strong className="fs-sm">Imaging:</strong>
              <ul className="fs-sm mb-0 mt-1">
                {step.imaging.map((img, idx) => (
                  <li key={idx}>{img.label}</li>
                ))}
              </ul>
            </div>
          )}
          {step.therapies && step.therapies.length > 0 && (
            <div className="mb-2">
              <strong className="fs-sm">Therapies:</strong>
              <ul className="fs-sm mb-0 mt-1">
                {step.therapies.map((therapy, idx) => (
                  <li key={idx}>{therapy.label}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {step.instructiontopatient && (
        <div className="mt-2">
          <p className="mb-0 fs-sm">
            <strong>Instructions:</strong> {step.instructiontopatient}
          </p>
        </div>
      )}
    </div>
  );
};

// Phase Block component
const PhaseBlock = ({
  phase,
  index,
  updatePhase,
  removePhase,
  addStepToPhase,
  editStep,
  deleteStep,
  loadContactsOptions,
}) => {
  const accordionId = `phase-accordion-${index}`;
  const collapseId = `phase-collapse-${index}`;
  const headingId = `heading-${index}`;

  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);

  // Auto-calculate end date when start date or days change for this phase
  useEffect(() => {
    if (phase.startdate && phase.days) {
      const startDateObj = new Date(phase.startdate);
      const endDateCalc = new Date(startDateObj);
      endDateCalc.setDate(startDateObj.getDate() + parseInt(phase.days));
      const calculatedEndDate = formatDateToInput(endDateCalc);

      if (phase.enddate !== calculatedEndDate) {
        updatePhase(index, "enddate", calculatedEndDate);
      }
    }
  }, [phase.startdate, phase.days, index, updatePhase]);

  // Auto-set start date when days are entered but no start date exists
  useEffect(() => {
    if (phase.days && !phase.startdate) {
      const today = new Date();
      const calculatedStartDate = formatDateToInput(today);
      updatePhase(index, "startdate", calculatedStartDate);
    }
  }, [phase.days, phase.startdate, index, updatePhase]);

  const handleAddStep = () => {
    setEditingStep(null);
    setShowStepModal(true);
  };

  const handleEditStep = (phaseIndex, stepIndex) => {
    const step = phase.steps[stepIndex];
    setEditingStep({ ...step, stepIndex });
    setShowStepModal(true);
  };

  const handleSaveStep = (stepData, phaseIndex, stepIndex) => {
    if (stepIndex !== undefined) {
      // Edit existing step
      editStep(index, stepIndex, stepData);
    } else {
      // Add new step
      addStepToPhase(index, stepData);
    }
  };

  const handleDeleteStep = (phaseIndex, stepIndex) => {
    deleteStep(index, stepIndex);
  };

  return (
    <>
      <div className="accordion mb-3" id={accordionId}>
        <div className="accordion-item border border-gray-300 rounded-8 shadow-sm">
          <h6
            className="accordion-header d-flex align-items-center"
            id={headingId}
          >
            <button
              className="accordion-button collapsed bg-light fw-semibold py-3 px-3 border-0 rounded-top-8 flex-grow-1"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#${collapseId}`}
              aria-expanded="false"
              aria-controls={collapseId}
            >
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="d-flex align-items-center">
                  <span className="text-primary me-2">Phase {index + 1}</span>
                  {phase.title && (
                    <span className="text-muted"> : {phase.title}</span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2 me-3">
                  {phase.steps && phase.steps.length > 0 && (
                    <span className="badge bg-info">
                      {phase.steps.length} steps
                    </span>
                  )}
                  {phase.days && (
                    <span className="badge bg-success">{phase.days} days</span>
                  )}
                </div>
              </div>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger ms-2 me-3 d-flex align-items-center"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                removePhase(index);
              }}
              title="Remove Phase"
              style={{ minWidth: "32px", height: "32px" }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "16px" }}
              >
                delete
              </span>
            </button>
          </h6>
          <div
            id={collapseId}
            className="accordion-collapse collapse"
            aria-labelledby={headingId}
            data-bs-parent={`#${accordionId}`}
          >
            <div className="accordion-body p-3 bg-white">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="d-block fw-semibold mb-2">
                    Phase Name *
                  </label>
                  <input
                    type="text"
                    value={phase.title || ""}
                    onChange={(e) =>
                      updatePhase(index, "title", e.target.value)
                    }
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: "white" }}
                    placeholder="Enter phase name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="d-block fw-semibold mb-2 text-dark">
                    Responsible <span className="text-danger">*</span>
                  </label>
                  <AsyncSelect
                    loadOptions={loadContactsOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    value={phase.responsibleperson}
                    onChange={(val) =>
                      updatePhase(index, "responsibleperson", val)
                    }
                    isClearable
                    placeholder="Search responsible person..."
                    styles={{
                      ...reactSelectStyle("3px", "6px", true, false),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: "white",
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="d-block fw-semibold mb-2">
                    Duration (Days) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={phase.days || ""}
                    onChange={(e) =>
                      updatePhase(index, "days", parseInt(e.target.value) || "")
                    }
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: "white" }}
                    placeholder="Enter duration in days"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fw-semibold mb-2">Start Date</label>
                  <input
                    type="date"
                    value={phase.startdate || ""}
                    onChange={(e) =>
                      updatePhase(index, "startdate", e.target.value)
                    }
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: "white" }}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fw-semibold mb-2">End Date</label>
                  <input
                    type="date"
                    value={phase.enddate || ""}
                    onChange={(e) =>
                      updatePhase(index, "enddate", e.target.value)
                    }
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: "white" }}
                    readOnly
                    title="Auto-calculated based on start date and duration"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">
                    Description *
                  </label>
                  <textarea
                    rows="3"
                    value={phase.description || ""}
                    onChange={(e) =>
                      updatePhase(index, "description", e.target.value)
                    }
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: "white" }}
                    placeholder="Brief description of this phase..."
                  />
                </div>
              </div>

              {/* Steps Section */}
              <div className="border-top pt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-semibold mb-0">Steps</h6>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                    onClick={handleAddStep}
                  >
                    <span className="material-symbols-outlined">add</span>
                    Add Steps
                  </button>
                </div>

                {phase.steps && phase.steps.length > 0 ? (
                  <div>
                    {phase.steps.map((step, stepIndex) => (
                      <StepBlock
                        key={stepIndex}
                        step={step}
                        stepIndex={stepIndex}
                        phaseIndex={index}
                        editStep={handleEditStep}
                        deleteStep={handleDeleteStep}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3 text-muted">
                    <span className="material-symbols-outlined fs-4">
                      assignment
                    </span>
                    <p className="mb-0 fs-sm">
                      No steps added yet. Click "Add Steps" to start.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Modal */}
      <StepModal
        isOpen={showStepModal}
        onClose={() => setShowStepModal(false)}
        onSave={handleSaveStep}
        editingStep={editingStep}
        phaseIndex={index}
        phaseStartDate={phase.startdate} // NEW: pass phase start date
        phaseDays={phase.days} // NEW: pass phase days for fallback
      />
    </>
  );
};

const SaveTreatmentTemplate = ({
  selectedTreatmentId,
  setSelectedTreatmentId,
  afterActionClose,
  setShowAlert,
  setAlertMessage,
  setMessageType,
  defaultPatient = null, // with value, label when creating for a specific patient
  defaultDoctor = null, // with value, label when creating by a specific doctor
  defaultClinic = null, // with value, label when creating by a specific clinic
}) => {
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const { t } = useTranslation();

  // Add loading state for data fetching
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Template Details states
  const [name, setName] = useState("");
  const [treatmentTemplate, setTreatmentTemplate] = useState(null);
  const [type, setType] = useState(null);

  const [specialty, setSpecialty] = useState(null);
  const [summary, setSummary] = useState("");
  const [treatmentstatus, setTreatmentstatus] = useState({});

  // Basic Details states - Add these new states
  const [patient, setPatient] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [clinic, setClinic] = useState(null);

  // Meta Data states - Updated for select arrays
  const [ageRange, setAgeRange] = useState({ min: "", max: "" });
  const [weightRange, setWeightRange] = useState({ min: "", max: "" });
  const [heightRange, setHeightRange] = useState({ min: "", max: "" });
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [days, setDays] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [progress, setProgress] = useState(0);
  // Template fields (for reference)
  const [knownAllergies, setKnownAllergies] = useState([]);
  const [knownMedication, setKnownMedication] = useState([]);
  const [knownMedicalHistory, setKnownMedicalHistory] = useState("");
  const [knownPastMedicalIssues, setKnownPastMedicalIssues] = useState([]);
  // Actual patient fields
  const [allergies, setAllergies] = useState([]);
  const [ongoingmedications, setOngoingmedications] = useState([]);
  const [pastmedicalhistory, setPastmedicalhistory] = useState("");
  const [pastmedicalissues, setPastmedicalissues] = useState([]);

  // Phases state
  const [phases, setPhases] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  // Auto-calculate start and end dates when days change or dates are empty
  useEffect(() => {
    if (days && (!startDate || !endDate)) {
      const today = new Date();
      const calculatedStartDate = formatDateToInput(today);

      // Calculate end date by adding days to start date
      const endDateCalc = new Date(today);
      endDateCalc.setDate(today.getDate() + parseInt(days));
      const calculatedEndDate = formatDateToInput(endDateCalc);

      if (!startDate) {
        setStartDate(calculatedStartDate);
      }
      if (!endDate) {
        setEndDate(calculatedEndDate);
      }
    }
  }, [days, startDate, endDate]);

  // Auto-update end date when start date or days change
  useEffect(() => {
    if (startDate && days) {
      const startDateObj = new Date(startDate);
      const endDateCalc = new Date(startDateObj);
      endDateCalc.setDate(startDateObj.getDate() + parseInt(days));
      const calculatedEndDate = formatDateToInput(endDateCalc);

      if (endDate !== calculatedEndDate) {
        setEndDate(calculatedEndDate);
      }
    }
  }, [startDate, days]);

  // Async load options for patient
  const loadPatientOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=PATIENT`;

      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  // Async load options for doctor
  const loadDoctorOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=DOCTOR`;

      const reponse = await getData(requestUrl);
      return reponse.data;
    } catch (error) {
      return [];
    }
  };

  // Async load options for company/clinic
  const loadClinicOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_COMPANY_LIST +
        `?token=${token}&filtername=${inputValue}`;
      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  // Async load options for specialty/category
  const loadSpecialtyOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_CATEGORIES +
        `?token=${token}&slug=medical-specialty&search=${inputValue}`;
      const response = await getData(requestUrl);
      return response.data.map((specialty) => ({
        value: specialty._id,
        label: specialty.name,
      }));
    } catch (error) {
      return [];
    }
  };

  // Async load options for treatment templates
  const loadTreatmentTemplateOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_TREATMENTS +
        `?token=${token}&modulename=treatmenttemplate&istemplate=1&search=${
          inputValue || ""
        }`;

      const response = await getData(requestUrl);

      if (response.status) {
        return response.data.map((template) => ({
          value: template._id,
          label: template.title,
          data: template, // Store full template data for potential pre-population
        }));
      }
      return [];
    } catch (error) {
      console.error("Error loading treatment templates:", error);
      return [];
    }
  };

  // Function to load template data and pre-populate form
  const loadTemplateDataAndPopulate = async (templateId) => {
    try {
      setIsLoadingData(true);

      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_TREATMENT_BY_ID +
        `/${templateId}?token=${token}`;

      console.log("Loading template data from:", requestUrl);

      const response = await getData(requestUrl);

      if (response.status) {
        const templateData = response.data;

        // Pre-populate template-based fields (keeping patient/doctor intact)
        if (!name) setName(templateData.title + " - Copy" || "");

        // Map treatmenttype value to the corresponding option object
        const treatmentTypeValue = templateData.medicalextension?.treatmenttype;
        const mappedType = treatmentTypeValue
          ? typeOptions.find((option) => option.value === treatmentTypeValue) ||
            null
          : null;
        if (mappedType) setType(mappedType);

        // Pre-populate company and specialty if not already set
        if (!clinic && templateData.companyid) {
          setClinic({
            value: templateData.companyid._id,
            label: templateData.companyid.name,
          });
        }
        if (!specialty && templateData.projecttypeid) {
          setSpecialty({
            value: templateData.projecttypeid._id,
            label: templateData.projecttypeid.name,
          });
        }

        setSummary(templateData.summary || "");

        // Set default status to active for new treatment plans
        if (!treatmentstatus)
          setTreatmentstatus({ value: "planned", label: "Planned" });

        // Pre-populate meta data
        setAgeRange(
          templateData.medicalextension
            ? {
                min: templateData.medicalextension.minage || "",
                max: templateData.medicalextension.maxage || "",
              }
            : { min: "", max: "" }
        );

        setWeightRange(
          templateData.medicalextension
            ? {
                min: templateData.medicalextension.minweight || "",
                max: templateData.medicalextension.maxweight || "",
              }
            : { min: "", max: "" }
        );

        setHeightRange(
          templateData.medicalextension
            ? {
                min: templateData.medicalextension.minheight || "",
                max: templateData.medicalextension.maxheight || "",
              }
            : { min: "", max: "" }
        );

        setAge(templateData.medicalextension?.age || "");
        setWeight(templateData.medicalextension?.weight || "");
        setHeight(templateData.medicalextension?.height || "");
        setDays(templateData.days || "");

        // Map multi-select values to option objects with labels
        const mappedAllergies = mapValuesToOptions(
          templateData.medicalextension?.knownallergies || [],
          allergyOptions
        );
        setKnownAllergies(mappedAllergies);

        const mappedMedications = mapValuesToOptions(
          templateData.medicalextension?.knownmedications || [],
          medicineOptions
        );
        setKnownMedication(mappedMedications);

        const mappedPastMedicalIssues = mapValuesToOptions(
          templateData.medicalextension?.knownpastmedicalissues || [],
          pastMedicalIssuesOptions
        );
        setKnownPastMedicalIssues(mappedPastMedicalIssues);

        setKnownMedicalHistory(
          templateData.medicalextension?.knownmedicalhistory ?? ""
        );

        // Pre-populate phases from template
        if (
          templateData.projectsections &&
          templateData.projectsections.length > 0
        ) {
          const templatePhases = templateData.projectsections.map((phase) => {
            // Map responsiblepersonrole as option object
            const mappedRole = mapValuesToSingleOption(
              phase.responsiblepersonrole,
              roleOptions
            );

            // Map steps data
            let mappedSteps = [];
            if (phase.projecttasks && phase.projecttasks.length > 0) {
              mappedSteps = phase.projecttasks.map((step) => ({
                _id: null, // Remove ID for new treatment plan
                title: step.title || step.stepName,
                interventiontype: step.medicalextension
                  ? mapValuesToSingleOption(
                      step.medicalextension.interventiontype,
                      interventionTypeOptions
                    )
                  : null,
                durationdays: step.medicalextension
                  ? step.medicalextension.durationdays || 0
                  : 0,
                instructiontopatient:
                  step.medicalextension?.instructiontopatient ?? "",
                medicines: mapValuesToOptions(
                  step.medicalextension?.medicines || [],
                  medicineOptions
                ),
                labTests: mapValuesToOptions(
                  step.medicalextension?.labTests || [],
                  labTestOptions
                ),
                imaging: mapValuesToOptions(
                  step.medicalextension?.imaging || [],
                  imagingOptions
                ),
                therapies: mapValuesToOptions(
                  step.medicalextension?.therapies || [],
                  therapyOptions
                ),
              }));
            }

            return {
              _id: null, // Remove ID for new treatment plan
              title: phase.title,
              days: phase.days,
              startdate: phase.startdate || "",
              enddate: phase.enddate || "",
              responsiblepersonrole: mappedRole,
              responsibleperson: null, // Start with no person selected for new plans
              description: phase.description,
              steps: mappedSteps,
            };
          });

          setPhases(templatePhases);
        }
      }
    } catch (error) {
      console.error("Error loading template data:", error);
      setAlertMessage("Error loading template data");
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Handler for treatment template selection
  const handleTreatmentTemplateChange = (selectedTemplate) => {
    setTreatmentTemplate(selectedTemplate);

    if (selectedTemplate && selectedTemplate.value) {
      // Load and populate template data
      loadTemplateDataAndPopulate(selectedTemplate.value);
    }
  };

  //function for get treatment template details
  const getTreatmentDetails = async () => {
    try {
      setIsLoading(true);

      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_TREATMENT_BY_ID +
        `/${selectedTreatmentId}?token=${token}`;

      console.log("Request URL for template details:", requestUrl);

      const response = await getData(requestUrl);

      console.log("Template details response:", response);

      if (response.status) {
        const treatmentData = response.data;

        // Map template details
        setName(treatmentData.title || "");

        // Map treatment template if exists
        if (treatmentData.treatmenttemplateid) {
          setTreatmentTemplate({
            value: treatmentData.treatmenttemplateid._id,
            label: treatmentData.treatmenttemplateid.title,
          });
        }

        // Map treatmenttype value to the corresponding option object
        const treatmentTypeValue =
          treatmentData.medicalextension?.treatmenttype;
        const mappedType = treatmentTypeValue
          ? typeOptions.find((option) => option.value === treatmentTypeValue) ||
            null
          : null;
        setType(mappedType);

        setSpecialty(
          treatmentData.projecttypeid
            ? {
                value: treatmentData.projecttypeid._id,
                label: treatmentData.projecttypeid.name,
              }
            : null
        );
        setSummary(treatmentData.summary || "");

        // Map treatment status
        if (
          treatmentData.medicalextension &&
          treatmentData.medicalextension.treatmentstatus
        ) {
          const mappedTreatmentStatus = mapValuesToSingleOption(
            treatmentData.medicalextension.treatmentstatus,
            treatmentStatusOptions
          );
          setTreatmentstatus(mappedTreatmentStatus);
        }

        // Map basic details
        if (treatmentData.patientdetails) {
          setPatient(treatmentData.patientdetails);
        }
        if (treatmentData.doctordetails) {
          setDoctor(treatmentData.doctordetails);
        }
        if (treatmentData.clinicdetails) {
          setClinic(treatmentData.clinicdetails);
        }

        setTreatmentTemplate(
          treatmentData.parent
            ? {
                value: treatmentData.parent._id,
                label: treatmentData.parent.title,
              }
            : null
        );

        // Map meta data
        setAgeRange(
          treatmentData.medicalextension
            ? {
                min: treatmentData.medicalextension.minage || "",
                max: treatmentData.medicalextension.maxage || "",
              }
            : { min: "", max: "" }
        );
        setWeightRange(
          treatmentData.medicalextension
            ? {
                min: treatmentData.medicalextension.minweight || "",
                max: treatmentData.medicalextension.maxweight || "",
              }
            : { min: "", max: "" }
        );
        setHeightRange(
          treatmentData.medicalextension
            ? {
                min: treatmentData.medicalextension.minheight || "",
                max: treatmentData.medicalextension.maxheight || "",
              }
            : { min: "", max: "" }
        );
        setAge(treatmentData.medicalextension?.age || "");
        setWeight(treatmentData.medicalextension?.weight || "");
        setHeight(treatmentData.medicalextension?.height || "");
        setDays(treatmentData.days || "");
        setStartDate(treatmentData.startdate || "");
        setEndDate(treatmentData.enddate || "");

        setProgress(treatmentData.progress || 0);

        // Map template fields (for reference)
        const mappedKnownAllergies = mapValuesToOptions(
          treatmentData.medicalextension?.knownallergies || [],
          allergyOptions
        );
        setKnownAllergies(mappedKnownAllergies);

        const mappedKnownMedications = mapValuesToOptions(
          treatmentData.medicalextension?.knownmedications || [],
          medicineOptions
        );
        setKnownMedication(mappedKnownMedications);

        const mappedKnownPastMedicalIssues = mapValuesToOptions(
          treatmentData.medicalextension?.knownpastmedicalissues || [],
          pastMedicalIssuesOptions
        );
        setKnownPastMedicalIssues(mappedKnownPastMedicalIssues);

        setKnownMedicalHistory(
          treatmentData.medicalextension?.knownmedicalhistory ?? ""
        );

        // Map actual patient fields
        const mappedAllergies = mapValuesToOptions(
          treatmentData.medicalextension?.allergies || [],
          allergyOptions
        );
        setAllergies(mappedAllergies);

        const mappedOngoingMedications = mapValuesToOptions(
          treatmentData.medicalextension?.ongoingmedications || [],
          medicineOptions
        );
        setOngoingmedications(mappedOngoingMedications);

        const mappedPastMedicalIssues = mapValuesToOptions(
          treatmentData.medicalextension?.pastmedicalissues || [],
          pastMedicalIssuesOptions
        );
        setPastmedicalissues(mappedPastMedicalIssues);

        setPastmedicalhistory(
          treatmentData.medicalextension?.pastmedicalhistory ?? ""
        );

        // Map phases
        setPhases(treatmentData.projectsections || []);

        // Add debugging for phases data
        if (
          treatmentData.projectsections &&
          treatmentData.projectsections.length > 0
        ) {
          treatmentData.projectsections.forEach((phase, index) => {
            // set responsiblepersonrole as option object
            const mappedRole = mapValuesToSingleOption(
              phase.responsiblepersonrole,
              roleOptions
            );
            phase.responsiblepersonrole = mappedRole;

            // Map actual responsible person if exists
            if (phase.responsibleperson) {
              phase.responsibleperson = {
                value: phase.responsibleperson._id,
                label:
                  `${phase.responsibleperson.name || ""} ${
                    phase.responsibleperson.surname || ""
                  }`.trim() ||
                  phase.responsibleperson.name ||
                  phase.responsibleperson.email,
              };
            } else {
              phase.responsibleperson = null;
            }

            // Set phase dates if they exist
            phase.startdate = phase.startdate || "";
            phase.enddate = phase.enddate || "";

            // Map steps data to correct field names
            if (phase.projecttasks && phase.projecttasks.length > 0) {
              console.log("phase.projecttasks :", phase.projecttasks);

              phase.steps = phase.projecttasks.map((step) => ({
                _id: step._id,
                title: step.title || step.stepName, // fallback if old field name exists
                interventiontype: step.medicalextension
                  ? mapValuesToSingleOption(
                      step.medicalextension.interventiontype,
                      interventionTypeOptions
                    )
                  : null,
                durationdays: step.medicalextension
                  ? step.medicalextension.durationdays || 0
                  : 0,
                instructiontopatient:
                  step.medicalextension?.instructiontopatient ?? "",
                medicines: mapValuesToOptions(
                  step.medicalextension?.medicines || [],
                  medicineOptions
                ),
                labTests: mapValuesToOptions(
                  step.medicalextension?.labTests || [],
                  labTestOptions
                ),
                imaging: mapValuesToOptions(
                  step.medicalextension?.imaging || [],
                  imagingOptions
                ),
                therapies: mapValuesToOptions(
                  step.medicalextension?.therapies || [],
                  therapyOptions
                ),
                startdate: step.startdate || "",
                enddate: step.enddate || "",
              }));

              console.log(
                `Mapped steps for phase @@@ ${index + 1}:`,
                phase.steps
              );
            }
          });
        }
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetHandler = () => {
    // Reset template details
    setName("");
    setTreatmentTemplate(null);
    setType(null);
    setSpecialty(null);
    setSummary("");
    setTreatmentstatus(null);

    // Reset basic details - but preserve defaults if available
    setPatient(defaultPatient);
    setDoctor(defaultDoctor);
    setClinic(defaultClinic);

    // Reset meta data
    setAgeRange({ min: "", max: "" });
    setWeightRange({ min: "", max: "" });
    setHeightRange({ min: "", max: "" });
    setAge("");
    setWeight("");
    setHeight("");
    setDays("");
    setStartDate("");
    setEndDate("");
    setProgress(0);
    // Reset template fields
    setKnownAllergies([]);
    setKnownMedication([]);
    setKnownMedicalHistory("");
    setKnownPastMedicalIssues([]);
    // Reset actual patient fields
    setAllergies([]);
    setOngoingmedications([]);
    setPastmedicalhistory("");
    setPastmedicalissues([]);

    // Reset phases
    setPhases([]);

    setSelectedTreatmentId(null);
  };

  // Custom styles for white background creatable selects
  const whiteCreatableSelectStyle = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
      borderColor: "#dee2e6",
      padding: "3px",
      borderRadius: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "white",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#007bff" : "white",
      "&:hover": {
        backgroundColor: state.isSelected ? "#007bff" : "#f8f9fa",
      },
    }),
  };

  //function for save treatment template
  const saveTreatmentHandler = async () => {
    try {
      setIsSaving(true);

      const apiData = {
        id: selectedTreatmentId,
        title: name,
        istemplate: false,
        parent: treatmentTemplate?.value,
        type: type?.value,
        company: clinic?.value,
        specialty: specialty?.value,
        summary: summary,
        status: status?.value,
        // Add basic details to API data
        patient: patient?.value,
        doctor: doctor?.value,
        clinic: clinic?.value,
        moderator: userInfo._id,
        ageRange: ageRange,
        weightRange: weightRange,
        heightRange: heightRange,
        age: age,
        weight: weight,
        height: height,
        days: days,
        startdate: startDate,
        enddate: endDate,
        progress: progress === "" ? 0 : progress, // NEW: include progress
        // Add template fields (for reference)
        knownAllergies: knownAllergies
          ? knownAllergies.map((allergy) => allergy.value)
          : [],
        knownMedication: knownMedication
          ? knownMedication.map((med) => med.value)
          : [],
        knownMedicalHistory: knownMedicalHistory,
        knownPastMedicalIssues: knownPastMedicalIssues
          ? knownPastMedicalIssues.map((issue) => issue.value)
          : [],
        // Add actual patient fields
        allergies: allergies ? allergies.map((allergy) => allergy.value) : [],
        ongoingmedications: ongoingmedications
          ? ongoingmedications.map((med) => med.value)
          : [],
        pastmedicalhistory: pastmedicalhistory,
        pastmedicalissues: pastmedicalissues
          ? pastmedicalissues.map((issue) => issue.value)
          : [],

        phases: phases.map((phase) => ({
          id: phase._id,
          title: phase.title,
          days: phase.days,
          startdate: phase.startdate,
          enddate: phase.enddate,
          responsiblepersonrole: phase.responsiblepersonrole?.value,
          responsibleperson: phase.responsibleperson?.value, // Add actual responsible person
          description: phase.description,
          steps: phase.steps
            ? phase.steps.map((step) => ({
                id: step._id,
                title: step.title,
                interventiontype: step.interventiontype?.value,
                durationdays: step.durationdays,
                instructiontopatient: step.instructiontopatient,
                medicines: step.medicines
                  ? step.medicines.map((med) => med.value)
                  : [],
                labTests: step.labTests
                  ? step.labTests.map((test) => test.value)
                  : [],
                imaging: step.imaging
                  ? step.imaging.map((img) => img.value)
                  : [],
                therapies: step.therapies
                  ? step.therapies.map((therapy) => therapy.value)
                  : [],
                surgery: step.surgery
                  ? step.surgery.map((surg) => surg.value)
                  : [],
              }))
            : [],
        })),
      };

      console.log("apiData", apiData);

      let requestUrl = url.API_BASE_URL;
      let response = {};

      requestUrl += url.API_SAVE_TREATMENT + `?token=${token}`;
      response = await postData(requestUrl, apiData);

      console.log("Response after saving template:", response);

      if (response.status) {
        setSelectedTreatmentId(response.data._id);
        setAlertMessage(t("treatment plan saved successfully"));
        setMessageType("success");
        setShowAlert(true);

        // //close offcanvas
        // bootstrap.Offcanvas.getInstance(
        //   document.querySelector("#saveTreatmentTemplateOffCanvas")
        // ).hide();
        // resetHandler();
        // afterActionClose([response.data]);
      }

      setIsSaving(false);
    } catch (error) {
      console.log(error.message);
      setIsSaving(false);
    }
  };

  // Add new phase to the array
  const addPhase = () => {
    const newPhase = {
      title: "",
      days: "",
      startdate: "",
      enddate: "",
      responsiblepersonrole: null,
      responsibleperson: null,
      description: "",
      steps: [],
    };
    setPhases([...phases, newPhase]);
  };

  // Remove phase from array
  const removePhase = (index) => {
    setPhases(phases.filter((_, i) => i !== index));
  };

  // Update specific phase
  const updatePhase = (index, field, value) => {
    const updatedPhases = [...phases];
    updatedPhases[index][field] = value;
    setPhases(updatedPhases);
  };

  // Add step to specific phase
  const addStepToPhase = (phaseIndex, stepData) => {
    const updatedPhases = [...phases];
    if (!updatedPhases[phaseIndex].steps) {
      updatedPhases[phaseIndex].steps = [];
    }
    updatedPhases[phaseIndex].steps.push(stepData);
    setPhases(updatedPhases);
  };

  // Edit step in specific phase
  const editStep = (phaseIndex, stepIndex, stepData) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].steps[stepIndex] = stepData;
    setPhases(updatedPhases);
  };

  // Delete step from specific phase
  const deleteStep = (phaseIndex, stepIndex) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].steps.splice(stepIndex, 1);
    setPhases(updatedPhases);
  };

  // Async load options for all contacts
  const loadContactsOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend`;

      console.log("Request URL for contacts:", requestUrl);

      const response = await getData(requestUrl);

      console.log("Contacts response data:", response);

      return response.data;
    } catch (error) {
      console.error("Error loading contacts:", error);
      return [];
    }
  };

  useEffect(() => {
    if (selectedTreatmentId) {
      getTreatmentDetails();
    } else {
      resetHandler();
    }
  }, [selectedTreatmentId]);

  useEffect(() => {
    if (defaultPatient && !selectedTreatmentId) {
      setPatient(defaultPatient);
    }
  }, [defaultPatient, selectedTreatmentId]);

  useEffect(() => {
    if (defaultDoctor && !selectedTreatmentId) {
      setDoctor(defaultDoctor);
    }
  }, [defaultDoctor, selectedTreatmentId]);

  useEffect(() => {
    if (defaultClinic && !selectedTreatmentId) {
      setClinic(defaultClinic);
    }
  }, [defaultClinic, selectedTreatmentId]);

  if (isLoadingData || isLoading) {
    return (
      <div
        className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
        tabIndex="-1"
        id="saveTreatmentTemplateOffCanvas"
        aria-labelledby="saveTreatmentTemplateOffCanvas"
      >
        {/* Header */}
        <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
          <div className="d-flex align-items-center gap-3">
            <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
              <span className="material-symbols-outlined text-primary fs-5 icon-md">
                medical_services
              </span>
            </div>
            <div>
              <h3 className="offcanvas-title fw-bold text-white mb-0">
                Generate/Update Treatment Plan
              </h3>
              <p className="text-white fs-sm mb-0">
                Add or update treatment template details
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={resetHandler}
          ></button>
        </div>

        <div className="offcanvas-body p-4">
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
            {/* Loading skeleton placeholder */}
            <div className="placeholder-glow d-flex flex-column gap-4">
              <span className="placeholder placeholder-lg bg-secondary col-12"></span>
              <span className="placeholder placeholder-lg bg-secondary col-8"></span>
              <span className="placeholder placeholder-lg bg-secondary col-4"></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveTreatmentTemplateOffCanvas"
      aria-labelledby="saveTreatmentTemplateOffCanvas"
    >
      {/* Header */}
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              medical_services
            </span>
          </div>
          <div>
            <h3 className="offcanvas-title fw-bold text-white mb-0">
              Generate/Update Treatment Plan
            </h3>
            <p className="text-white fs-sm mb-0">
              Add or update treatment plan details
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>

      <div className="offcanvas-body p-4">
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Basic Details Section */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="mb-3 fw-semibold">
                {t("Responsible Doctor & Patient")}
              </h5>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Patient <span className="text-danger">*</span>
                  </label>
                  <AsyncSelect
                    loadOptions={loadPatientOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    value={patient}
                    onChange={setPatient}
                    isClearable
                    placeholder="Search patient..."
                    styles={reactSelectStyle("3px", "6px", true, false)}
                  />
                  {patient ? <PatientDetailsCard patient={patient} /> : null}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Doctor <span className="text-danger">*</span>
                  </label>
                  <AsyncSelect
                    loadOptions={loadDoctorOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    value={doctor}
                    onChange={setDoctor}
                    isClearable
                    placeholder="Search doctor..."
                    styles={reactSelectStyle("3px", "6px", true, false)}
                  />
                  {doctor ? <DoctorDetailsCard doctor={doctor} /> : null}
                </div>
              </div>
            </div>

            {/* Template Details Section */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="mb-3 fw-semibold">{t("Treatment Details")}</h5>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Based on Treatment Template
                  </label>
                  {selectedTreatmentId ? (
                    // Show read-only template name when editing existing treatment
                    <div
                      className="form-control fs-sm border border-gray-300 bg-light text-muted d-flex align-items-center"
                      style={{ minHeight: "38px" }}
                    >
                      {treatmentTemplate
                        ? treatmentTemplate.label
                        : "No template selected"}
                    </div>
                  ) : (
                    // Show AsyncSelect when creating new treatment
                    <AsyncSelect
                      loadOptions={loadTreatmentTemplateOptions}
                      defaultOptions={true}
                      cacheOptions={false}
                      value={treatmentTemplate}
                      onChange={handleTreatmentTemplateChange}
                      isClearable
                      placeholder="Search and select treatment template..."
                      styles={reactSelectStyle("3px", "6px", true, false)}
                    />
                  )}
                  <small className="text-muted">
                    {selectedTreatmentId
                      ? "Template selection is locked when editing existing treatment plans"
                      : "Optional: Select an existing treatment template to base this treatment plan on"}
                  </small>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Treatment Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: "white" }}
                    placeholder="Enter template name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={typeOptions}
                    value={type}
                    onChange={setType}
                    isClearable
                    placeholder="Select template type"
                    styles={reactSelectStyle("3px", "6px", true, false)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Clinic
                  </label>
                  <AsyncSelect
                    loadOptions={loadClinicOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    value={clinic}
                    onChange={setClinic}
                    isClearable
                    placeholder="Search or select company..."
                    styles={reactSelectStyle("3px", "6px", true, false)}
                  />
                  {clinic ? <ClinicDetailsCard clinic={clinic} /> : null}
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Specialty <span className="text-danger">*</span>
                  </label>
                  <AsyncSelect
                    loadOptions={loadSpecialtyOptions}
                    defaultOptions={true}
                    cacheOptions={false}
                    value={specialty}
                    onChange={setSpecialty}
                    isClearable
                    placeholder="Search or select specialty..."
                    styles={reactSelectStyle("3px", "6px", true, false)}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Treatment Status <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={treatmentStatusOptions}
                    value={treatmentstatus}
                    onChange={setTreatmentstatus}
                    isClearable
                    placeholder="Select status"
                    styles={reactSelectStyle("3px", "6px", true, false)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Summary
                  </label>
                  <textarea
                    rows="3"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: "white" }}
                    placeholder="Enter template summary"
                  />
                </div>
              </div>
            </div>

            {/* Meta Data Section */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="mb-3 fw-semibold">{t("Meta Data")}</h5>

              {/* Patient Actual Measurements */}
              <div className="mb-4">
                <h6 className="fw-semibold text-primary mb-3">
                  Patient Measurements
                </h6>
                <div className="row">
                  <div className="col-md-3 mb-3">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      Patient Age (years)
                      {ageRange.min && ageRange.max && (
                        <span className="text-muted fw-normal">
                          {" "}
                          - Template Range: {ageRange.min}-{ageRange.max}
                        </span>
                      )}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="150"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: "white" }}
                      placeholder="Enter patient's age"
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      Patient Weight (kg)
                      {weightRange.min && weightRange.max && (
                        <span className="text-muted fw-normal">
                          {" "}
                          - Template Range: {weightRange.min}-{weightRange.max}
                        </span>
                      )}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: "white" }}
                      placeholder="Enter patient's weight"
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      Patient Height (cm)
                      {heightRange.min && heightRange.max && (
                        <span className="text-muted fw-normal">
                          {" "}
                          - Template Range: {heightRange.min}-{heightRange.max}
                        </span>
                      )}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: "white" }}
                      placeholder="Enter patient's height"
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      Total Days
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={days}
                      onChange={(e) => setDays(e.target.value)}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: "white" }}
                      placeholder="Enter total days"
                    />
                  </div>
                </div>
              </div>

              {/* Treatment Timeline & Progress */}
              <div className="mb-4">
                <h6 className="fw-semibold text-success mb-3">
                  Treatment Timeline
                </h6>
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: "white" }}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: "white" }}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                      Progress (0-100) %
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={progress}
                      onChange={(e) =>
                        setProgress(validateProgress(e.target.value))
                      }
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: "white" }}
                      placeholder="Enter treatment progress"
                    />
                    <small className="text-muted">
                      Progress must be between 0 and 100%
                    </small>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Patient Allergies
                  </label>
                  <CreatableSelect
                    isMulti
                    options={allergyOptions}
                    value={allergies}
                    onChange={setAllergies}
                    isClearable
                    placeholder="Select or create patient's actual allergies..."
                    styles={whiteCreatableSelectStyle}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                  {knownAllergies && knownAllergies.length > 0 && (
                    <div className="mt-2 p-2 bg-light rounded">
                      <small className="text-muted fw-semibold">
                        Template Known Allergies:
                      </small>
                      <div className="mt-1">
                        {knownAllergies.map((allergy, idx) => (
                          <span
                            key={idx}
                            className="badge bg-warning text-dark me-1 mb-1"
                          >
                            {allergy.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-md-12 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Ongoing Medications
                  </label>
                  <CreatableSelect
                    isMulti
                    options={medicineOptions}
                    value={ongoingmedications}
                    onChange={setOngoingmedications}
                    isClearable
                    placeholder="Select or create patient's ongoing medications..."
                    styles={whiteCreatableSelectStyle}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                  {knownMedication && knownMedication.length > 0 && (
                    <div className="mt-2 p-2 bg-light rounded">
                      <small className="text-muted fw-semibold">
                        Template Known Medications:
                      </small>
                      <div className="mt-1">
                        {knownMedication.map((med, idx) => (
                          <span
                            key={idx}
                            className="badge bg-info text-white me-1 mb-1"
                          >
                            {med.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-md-12 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Past Medical Issues
                  </label>
                  <CreatableSelect
                    isMulti
                    options={pastMedicalIssuesOptions}
                    value={pastmedicalissues}
                    onChange={setPastmedicalissues}
                    isClearable
                    placeholder="Select or create patient's past medical issues..."
                    styles={whiteCreatableSelectStyle}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                  {knownPastMedicalIssues &&
                    knownPastMedicalIssues.length > 0 && (
                      <div className="mt-2 p-2 bg-light rounded">
                        <small className="text-muted fw-semibold">
                          Template Known Past Medical Issues:
                        </small>
                        <div className="mt-1">
                          {knownPastMedicalIssues.map((issue, idx) => (
                            <span
                              key={idx}
                              className="badge bg-secondary me-1 mb-1"
                            >
                              {issue.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Past Medical History
                  </label>
                  <textarea
                    rows="3"
                    value={pastmedicalhistory}
                    onChange={(e) => setPastmedicalhistory(e.target.value)}
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: "white" }}
                    placeholder="Enter patient's detailed past medical history..."
                  />
                  {knownMedicalHistory && knownMedicalHistory.trim() && (
                    <div className="mt-2 p-2 bg-light rounded">
                      <small className="text-muted fw-semibold">
                        Template Known Medical History:
                      </small>
                      <p className="mb-0 mt-1 fs-sm text-dark">
                        {knownMedicalHistory}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Treatment Phases Section */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fw-semibold">{t("Treatment Phases")}</h5>
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                  onClick={addPhase}
                >
                  <span className="material-symbols-outlined me-1">add</span>
                  Add Phase
                </button>
              </div>

              {phases.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <span className="material-symbols-outlined fs-1">
                    timeline
                  </span>
                  <p className="mb-0">
                    No phases added yet. Click "Add Phase" to start.
                  </p>
                </div>
              ) : (
                <>
                  {phases.map((phase, index) => (
                    <PhaseBlock
                      key={index}
                      phase={phase}
                      index={index}
                      updatePhase={updatePhase}
                      removePhase={removePhase}
                      addStepToPhase={addStepToPhase}
                      editStep={editStep}
                      deleteStep={deleteStep}
                      loadContactsOptions={loadContactsOptions}
                    />
                  ))}

                  {/* Add Phase button at the bottom */}
                  <div className="d-flex justify-content-center mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                      onClick={addPhase}
                    >
                      <span className="material-symbols-outlined">add</span>
                      Add Another Phase
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="group_btnOuter d-flex justify-content-end pt-3 border-top mt-auto">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={saveTreatmentHandler}
                disabled={isSaving}
                style={{
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-1">save</span>
                    Save Treatment Plan
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SaveTreatmentTemplate;
